import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, X, ChevronLeft, ChevronRight, Camera, Sparkles, Send } from 'lucide-react';
import { toast } from 'sonner';
import { useParallax, staggerContainer, staggerItem, scaleIn, slideLeft, slideRight } from '../hooks/useAnimations';
import LazyImage from '../components/LazyImage';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { id: 'championships', title: 'Campeonatos & Eventos VIP', desc: 'Acceso privilegiado a The Masters, The Open, Ryder Cup y los eventos de golf mas exclusivos del mundo.', img: 'https://images.pexels.com/photos/8454632/pexels-photo-8454632.jpeg?auto=compress&cs=tinysrgb&w=1200' },
  { id: 'legendary', title: 'Campos Legendarios del Mundo', desc: 'St Andrews, Augusta, Pebble Beach, Royal Melbourne. Los campos que todo golfista suena jugar.', img: 'https://images.unsplash.com/photo-1672871583040-42826d4e9ca4?q=80&w=1200&auto=format&fit=crop' },
  { id: 'retreats', title: 'Retiros Privados de Golf', desc: 'Escapadas exclusivas con total privacidad. Campos reservados, villas privadas, servicio discreto.', img: 'https://static.prod-images.emergentagent.com/jobs/7621458b-ca63-4125-ad1b-115cf548682c/images/ca7cf37d3af34a14ef9535c7ef238bf0f220cc3b1fb9d3f8b6f6a609b29b6d56.png' },
  { id: 'corporate', title: 'Golf Corporativo', desc: 'Retiros ejecutivos que combinan networking de alto nivel con golf de campeonato.', img: 'https://images.unsplash.com/photo-1540575467063-178a50c2df87?q=80&w=1200&auto=format&fit=crop' },
  { id: 'father-son', title: 'Padre e Hijo', desc: 'Experiencias disenadas para crear recuerdos generacionales en los mejores campos del mundo.', img: 'https://images.pexels.com/photos/33245231/pexels-photo-33245231.jpeg?auto=compress&cs=tinysrgb&w=1200' },
  { id: 'lifestyle', title: 'Golf & Lifestyle de Lujo', desc: 'Golf combinado con gastronomia, yates privados, spa y experiencias que trascienden el juego.', img: 'https://static.prod-images.emergentagent.com/jobs/7621458b-ca63-4125-ad1b-115cf548682c/images/474fcaae205b7391782b6f63001d9cad475a62c831d1dc12ea6e58f1abe5cbc8.png' },
  { id: 'invitational', title: 'Acceso Invitacional & Exclusivo', desc: 'Campos members-only, clubes privados y acceso que normalmente requiere invitacion.', img: 'https://images.unsplash.com/photo-1672871582992-1b30b19878f5?q=80&w=1200&auto=format&fit=crop' },
];

const GALLERY_IMAGES = [
  { src: 'https://images.unsplash.com/photo-1735944242474-996f75ee6192?q=80&w=2070&auto=format&fit=crop', alt: 'Campo de golf con vista al oceano', category: 'Campos' },
  { src: 'https://images.unsplash.com/photo-1667842288007-ea49b67ce9cf?q=80&w=2070&auto=format&fit=crop', alt: 'Resort con piscina premium', category: 'Lifestyle' },
  { src: 'https://images.unsplash.com/flagged/photo-1576448356254-bf22cfc80304?q=80&w=2070&auto=format&fit=crop', alt: 'Golfista profesional en accion', category: 'Golf' },
  { src: 'https://images.unsplash.com/photo-1629928520300-56a44e245611?q=80&w=2070&auto=format&fit=crop', alt: 'Atardecer en el campo de golf', category: 'Campos' },
  { src: 'https://images.pexels.com/photos/32330644/pexels-photo-32330644.jpeg?auto=compress&cs=tinysrgb&w=1200', alt: 'Yate de lujo en el Caribe', category: 'Lifestyle' },
  { src: 'https://images.unsplash.com/photo-1551918120-9739cb430c6d?q=80&w=2070&auto=format&fit=crop', alt: 'Piscina infinita tropical', category: 'Lifestyle' },
  { src: 'https://images.unsplash.com/photo-1560092222-ac4e6d68dfd4?q=80&w=2070&auto=format&fit=crop', alt: 'Vista aerea campo junto al mar', category: 'Campos' },
  { src: 'https://images.unsplash.com/photo-1600609293076-fe896757596d?q=80&w=2070&auto=format&fit=crop', alt: 'Swing profesional', category: 'Golf' },
  { src: 'https://static.prod-images.emergentagent.com/jobs/7621458b-ca63-4125-ad1b-115cf548682c/images/474fcaae205b7391782b6f63001d9cad475a62c831d1dc12ea6e58f1abe5cbc8.png', alt: 'Llegada al club de lujo', category: 'Lifestyle' },
  { src: 'https://images.unsplash.com/photo-1766302524641-0961724b1781?q=80&w=2070&auto=format&fit=crop', alt: 'Piscina rodeada de vegetacion', category: 'Lifestyle' },
  { src: 'https://images.unsplash.com/photo-1587174506484-2f3807308e0d?q=80&w=2070&auto=format&fit=crop', alt: 'Golfista al atardecer', category: 'Golf' },
  { src: 'https://images.unsplash.com/photo-1715242563833-946f4b811399?q=80&w=2070&auto=format&fit=crop', alt: 'Vista al oceano desde resort', category: 'Lifestyle' },
];

const GALLERY_CATS = ['Todas', 'Campos', 'Golf', 'Lifestyle'];

function ParallaxExperienceImage({ src, alt }) {
  const { ref, y } = useParallax(40);
  return (
    <div ref={ref} className="relative aspect-[16/10] overflow-hidden bg-[#0A120E]">
      <motion.img src={src} alt={alt} style={{ y }} className="w-full h-[130%] object-cover" loading="lazy" />
    </div>
  );
}

export default function ExperiencesPage() {
  const [galleryFilter, setGalleryFilter] = useState('Todas');
  const [lightboxIdx, setLightboxIdx] = useState(-1);
  const [aiQuery, setAiQuery] = useState('');
  const [aiResult, setAiResult] = useState('');
  const [aiLoading, setAiLoading] = useState(false);

  const handleAI = async (e) => {
    e.preventDefault();
    if (!aiQuery.trim()) return;
    setAiLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/ai/recommendations`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ preferences: aiQuery })
      });
      const data = await res.json();
      setAiResult(data.recommendations);
    } catch { toast.error('Error al obtener recomendaciones'); }
    finally { setAiLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#050505] pt-20" data-testid="experiences-page">
      {/* Header with text reveal */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={staggerContainer}>
            <motion.p variants={staggerItem} className="overline mb-3">Experiencias Globales</motion.p>
            <motion.h1 variants={staggerItem} className="text-5xl md:text-6xl lg:text-7xl font-bold tracking-tighter text-white overflow-hidden" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
              <motion.span className="inline-block" initial={{ y: 80 }} whileInView={{ y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }}>
                Cada Experiencia
              </motion.span>
              <br />
              <motion.span className="inline-block" initial={{ y: 80 }} whileInView={{ y: 0 }} viewport={{ once: true }} transition={{ delay: 0.1, duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }}>
                es Unica.
              </motion.span>
            </motion.h1>
            <motion.p variants={staggerItem} className="text-base md:text-lg text-zinc-400 mt-6 max-w-xl">
              No ofrecemos paquetes. Disenamos experiencias de golf personalizadas en los campos mas exclusivos del mundo.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Experience Categories with alternating slide + parallax */}
      <section className="pb-24 md:pb-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 space-y-20">
          {CATEGORIES.map((cat, idx) => {
            const isReversed = idx % 2 === 1;
            return (
              <motion.div key={cat.id}
                initial="hidden" whileInView="show" viewport={{ once: true, margin: '-80px' }}
                variants={staggerContainer}
                className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-center"
                data-testid={`exp-cat-${cat.id}`}>
                <motion.div variants={isReversed ? slideRight : slideLeft} className={isReversed ? 'md:order-2' : ''}>
                  <ParallaxExperienceImage src={cat.img} alt={cat.title} />
                </motion.div>
                <motion.div variants={isReversed ? slideLeft : slideRight} className={`${isReversed ? 'md:order-1' : ''} space-y-4`}>
                  <motion.h2 className="text-2xl md:text-3xl font-bold tracking-tight text-white overflow-hidden" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
                    <motion.span className="inline-block" initial={{ y: 60 }} whileInView={{ y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7, ease: [0.25, 0.46, 0.45, 0.94] }}>
                      {cat.title}
                    </motion.span>
                  </motion.h2>
                  <p className="text-base text-zinc-400 leading-relaxed">{cat.desc}</p>
                  <p className="text-xs text-zinc-600 uppercase tracking-wider">Cada experiencia es personalizada</p>
                  <Link to="/contacto" className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-white transition-colors pt-2 group" data-testid={`exp-cta-${cat.id}`}>
                    Planear Mi Experiencia <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
                  </Link>
                </motion.div>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* Photo Gallery with stagger */}
      <section className="py-24 md:py-32 bg-[#0A120E]" data-testid="photo-gallery">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={staggerContainer} className="text-center mb-12">
            <motion.div variants={staggerItem}><Camera className="w-6 h-6 text-zinc-500 mx-auto mb-4" /></motion.div>
            <motion.h2 variants={staggerItem} className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-white" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Galeria</motion.h2>
            <motion.p variants={staggerItem} className="text-base text-zinc-400 mt-3 max-w-lg mx-auto">Momentos que definen la experiencia Elite Golf MX.</motion.p>
          </motion.div>
          <div className="flex flex-wrap justify-center gap-2 mb-10">
            {GALLERY_CATS.map(cat => (
              <button key={cat} onClick={() => setGalleryFilter(cat)}
                className={`px-4 py-2 text-sm transition-all duration-300 ${galleryFilter === cat ? 'bg-white text-black font-medium' : 'border border-white/10 text-zinc-400 hover:text-white hover:border-white/25'}`}
                data-testid={`gallery-filter-${cat.toLowerCase()}`}>{cat}</button>
            ))}
          </div>
          <div className="columns-2 md:columns-3 gap-4 space-y-4">
            {GALLERY_IMAGES.filter(img => galleryFilter === 'Todas' || img.category === galleryFilter).map((img, i) => {
              const globalIdx = GALLERY_IMAGES.indexOf(img);
              const isLarge = i % 5 === 0 || i % 5 === 3;
              return (
                <motion.div key={img.src} initial={{ opacity: 0, scale: 0.92 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }}
                  transition={{ delay: i * 0.06, duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] }}
                  className="break-inside-avoid cursor-pointer group relative overflow-hidden"
                  onClick={() => setLightboxIdx(globalIdx)} data-testid={`gallery-image-${i}`}>
                  <LazyImage src={img.src} alt={img.alt} aspectClass={isLarge ? 'aspect-[3/4]' : 'aspect-square'} />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all duration-500 flex items-end">
                    <div className="p-4 translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-out">
                      <p className="text-xs uppercase tracking-wider text-white/70">{img.category}</p>
                      <p className="text-sm text-white font-medium mt-0.5">{img.alt}</p>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Lightbox */}
      <AnimatePresence>
        {lightboxIdx >= 0 && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center" onClick={() => setLightboxIdx(-1)} data-testid="lightbox-overlay">
            <button onClick={() => setLightboxIdx(-1)} className="absolute top-6 right-6 text-white/60 hover:text-white transition-colors z-10 p-2" data-testid="lightbox-close"><X size={28} /></button>
            <button onClick={(e) => { e.stopPropagation(); setLightboxIdx(prev => prev <= 0 ? GALLERY_IMAGES.length - 1 : prev - 1); }} className="absolute left-4 md:left-8 text-white/40 hover:text-white transition-colors z-10 p-3" data-testid="lightbox-prev"><ChevronLeft size={32} /></button>
            <button onClick={(e) => { e.stopPropagation(); setLightboxIdx(prev => prev >= GALLERY_IMAGES.length - 1 ? 0 : prev + 1); }} className="absolute right-4 md:right-8 text-white/40 hover:text-white transition-colors z-10 p-3" data-testid="lightbox-next"><ChevronRight size={32} /></button>
            <motion.div key={lightboxIdx} initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.3 }} className="max-w-5xl max-h-[85vh] w-full mx-4" onClick={e => e.stopPropagation()}>
              <img src={GALLERY_IMAGES[lightboxIdx]?.src} alt={GALLERY_IMAGES[lightboxIdx]?.alt} className="w-full h-full max-h-[80vh] object-contain" />
              <div className="text-center mt-4">
                <p className="text-sm text-white">{GALLERY_IMAGES[lightboxIdx]?.alt}</p>
                <p className="text-xs text-zinc-500 mt-1">{lightboxIdx + 1} / {GALLERY_IMAGES.length}</p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* AI Concierge with stagger */}
      <section className="py-24 md:py-32 bg-[#050505]" data-testid="ai-recommendations">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={staggerContainer} className="text-center mb-10">
            <motion.div variants={staggerItem}><Sparkles className="w-6 h-6 text-zinc-500 mx-auto mb-4" /></motion.div>
            <motion.h2 variants={staggerItem} className="text-3xl md:text-4xl font-bold tracking-tight text-white" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Concierge Inteligente</motion.h2>
            <motion.p variants={staggerItem} className="text-base text-zinc-400 mt-3">Describenos tu experiencia ideal y nuestro asistente te dara inspiracion personalizada.</motion.p>
          </motion.div>
          <motion.form onSubmit={handleAI} className="space-y-4"
            initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.3, duration: 0.6 }}>
            <textarea value={aiQuery} onChange={e => setAiQuery(e.target.value)}
              placeholder="Ej: Quiero jugar los mejores links de Escocia con mi padre, con servicio de concierge completo..."
              className="w-full bg-white/5 border border-white/10 px-5 py-4 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-white/30 min-h-[120px] resize-none transition-colors duration-300"
              data-testid="ai-query-input" />
            <button type="submit" disabled={aiLoading}
              className="w-full bg-white text-black py-4 text-sm font-medium hover:bg-zinc-200 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
              data-testid="ai-submit-button">
              {aiLoading ? (<><div className="w-4 h-4 border-2 border-black/20 border-t-black rounded-full animate-spin" /> Generando...</>) : (<><Send size={16} /> Obtener Inspiracion</>)}
            </button>
          </motion.form>
          {aiResult && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mt-8 p-6 border border-white/10 bg-[#0A120E]" data-testid="ai-result">
              <p className="text-sm text-zinc-300 whitespace-pre-wrap leading-relaxed">{aiResult}</p>
            </motion.div>
          )}
        </div>
      </section>
    </div>
  );
}
